import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';

import { Device } from '@capacitor/device';

import CommonButton from '../../../buttons/common-button';
import InfoBox from '../../../blocks/messages/info-box';
import ToggleButton from '../../../buttons/toggle-button';

import { useCameraPhoto } from '../../../../modules/cameraModule'

import MediasService from '../../../../services/MediasService';
import CustomModal from '../../../blocks/custom-modal';

import { AddCircle, Send, Undo } from '@mui/icons-material';

import { useMediaContext } from '../../../../context/media-context';

/**
 * MediaUploadForm Component
 * 
 * This component renders a form for uploading media files.
 *
 * @param {Array} media - The media file to upload
 * @param {function} setMedia - The function to update the media file
 * @param {object} folder - The folder to upload the media file to
 * @param {boolean} geolocation - The geolocation data for the media file
 * @param {boolean} timestamp - The timestamp data for the media file
 * @param {function} onGeolocationChange - Callback function to handle geolocation changes
 * @param {function} onTimestampChange - Callback function to handle timestamp changes
 * 
 * @returns {JSX.Element}
 */
const MediaUploadForm = ({
    media,
    setMedia,
    folder,
    geolocation,
    timestamp,
    onGeolocationChange,
    onTimestampChange,
    inAppointment = false,
}) => {
    // Import the useCameraPhoto hook from the camera module
    const { savePhoto } = useCameraPhoto();

    // State variables for media categories and selected category
    const [mediaCategories, setMediaCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [loading, setLoading] = useState(false);

    const [successMessages, setSuccessMessages] = useState([]);
    const [errorMessages, setErrorMessages] = useState([]);

    // State variable for the media files
    const [mediasQueue, setMediasQueue] = useState([]);

    // State variable for the preview modal
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [isConfirmBackOpen, setIsConfirmBackOpen] = useState(false);

    // Hook for navigation
    const navigate = useNavigate();

    // Import the media context to manage unsent media and media page state
    const { unsentMedia, setUnsentMedia } = useMediaContext();

    /**
     * fetchMediaCategories
     * 
     * This asynchronous function fetches media categories from the API
     * and updates the state with the results.
     * 
     * @returns {Promise<void>}
     */
    const fetchMediaCategories = async () => {
        try {
            // Set loading state to true
            setLoading(true);

            // Fetch media categories from the API
            const categories = await MediasService.getMediaCategories();

            // Check if categories are found
            if (!categories || categories.length === 0) {
                setErrorMessages(['Aucune catégorie trouvée.']);
                return;
            }

            // Update the state with the fetched categories
            setMediaCategories(categories);
        } catch (error) {
            setErrorMessages([error?.message || 'Une erreur inattendue est survenue.']);
        } finally {
            setLoading(false);
        }
    };

    /**
     * useEffect for media capture
     * This effect runs when the `media` prop changes.
     * It sets the `unsentMedia
     */
    useEffect(() => {
        if (media) {
            setUnsentMedia(true); // Set unsent media to true when media is captured
        } else if (mediasQueue.length > 0) {
            setUnsentMedia(true); // Set unsent media to true when there are media in the queue
        } else if (!media && mediasQueue.length === 0) {
            setUnsentMedia(false); // Reset unsent media when no media is captured
        }
    }, [media, mediasQueue]);

    /**
     * Update group when category changes
     */
    useEffect(() => {
        if (selectedCategory) {
            const category = mediaCategories.find(cat => cat.id === parseInt(selectedCategory, 10));
            if (category?.media_group_category?.name) {
                setSelectedGroup(category.media_group_category.name);
            }
        }
    }, [selectedCategory, mediaCategories]);

    /**
     * handlePreview
     * 
     * This function handles the "Prévisualiser" button click.
     * It triggers the onImageUpload callback to send the captured media
     * and all form data back to the parent component.
     */
    const handleConfirm = () => {

        setErrorMessages([]); // Clear error messages
        setSuccessMessages([]); // Clear success messages

        // Check if an media has been captured
        if (!media && mediasQueue.length === 0) {
            setErrorMessages(['Veuillez capturer un média avant de continuer.']);
            return;
        }

        if (media && !selectedCategory) {
            setErrorMessages(['Veuillez sélectionner une catégorie.']);
            return;
        }

        // Check if a category has been selected
        const cat = mediaCategories.find(c => c.id === parseInt(selectedCategory, 10));
        if (!selectedCategory && !cat) {
            setErrorMessages(['Veuillez sélectionner une catégorie.']);
            return;
        }

        setMediasQueue((prevMedias) => [
            ...prevMedias,
            {
                media: media,
                categoryId: cat ? cat.id : 0,
                categoryName: cat ? cat.name : '',
            }
        ]);
        setMedia(null);

        // Find the folder by ID
        if (!folder) {
            setErrorMessages(['Le dossier est introuvable.']);
            return;
        }

        setIsPreviewOpen(true);
    };

    /**
     * handleDeleteMedia
     * 
     * This function deletes a media item from the medias state array.
     *
     * @param {number} index - The index of the media item to delete.
     * @returns {void}
     */
    const handleDeleteMedia = (index) => {
        setMediasQueue((prevMedias) => prevMedias.filter((_, i) => i !== index));
    };

    /**
     * handleAddMediasQueue
     * 
     * This function adds the captured media to the medias state array.
     * It is triggered when the "Ajouter un autre média" button is clicked.
     * 
     * @returns {void}
     */
    const handleAddMediasQueue = () => {
        setErrorMessages([]); // Clear error messages
        setSuccessMessages([]); // Clear success messages

        // Check if an media has been captured
        if (!media) {
            setErrorMessages(['Veuillez capturer un média avant de continuer.']);
            return;
        }

        // Check if a category has been selected
        if (!selectedCategory) {
            setErrorMessages(['Veuillez sélectionner une catégorie.']);
            return;
        }

        // Find the folder by ID
        if (!folder) {
            setErrorMessages(['Le dossier est introuvable.']);
            return;
        }

                // Check if a category has been selected
        const cat = mediaCategories.find(c => c.id === parseInt(selectedCategory, 10));
        if (!selectedCategory && !cat) {
            setErrorMessages(['Veuillez sélectionner une catégorie.']);
            return;
        }

        setMediasQueue((prevMedias) => [
            ...prevMedias,
            {
                media: media,
                categoryId: cat ? cat.id : 0,
                categoryName: cat ? cat.name : '',
            }
        ]);
        setMedia(null);
    }

    /**
     * secureBackNavigate
     * This function handles the back navigation with a confirmation dialog
     * if there are unsent media files
     * @returns {void}
     */
    const secureBackNavigate = () => {
        // Check if there are unsent media files
        if (unsentMedia) {
            setIsConfirmBackOpen(true); // Open confirmation dialog
        } else {
            navigate(-1); // Navigate back if no unsent media
        }
    };

    /**
     * handleSave
     * 
     * This function handles the "Associer le média au dossier" button click.
     * It triggers the onImageUpload callback to send the captured media 
     * and all form data back to the parent component.
     */
    const handleSave = async () => {

        setErrorMessages([]); // Clear error messages
        setSuccessMessages([]); // Clear success messages

        // Get the device information
        const deviceInfo = await Device.getInfo();
        if (!deviceInfo) {
            setErrorMessages(['Impossible de récupérer les informations du périphérique.']);
            return;
        }

        // Loop through the media files and upload them to the API
        mediasQueue.forEach(async mediaItem => {
            const payload = {
                file: mediaItem.media.file,
                id_folder: folder.id,
                id_media_category: parseInt(mediaItem.categoryId, 10),
            }; // Create a payload object to send to the API

            try {
                // Save the photo to the device if it is an iOS or Android device
                if (['ios', 'android'].includes(deviceInfo.platform) && mediaItem.media && mediaItem.media.type === 'image') {
                    await savePhoto(mediaItem.media.file, folder.folder_number);
                }

                const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

                // Delay the operation for 1 second
                await delay(1000);
                
                // Upload the media file to the API
                await MediasService.uploadMedia(payload);

                // Successfully uploaded the media file
                setSuccessMessages(prev => [
                ...prev,
                `L'image a été importée avec succès dans la catégorie « ${mediaItem.categoryName} ».`
                ]);
            } catch (error) {
                setErrorMessages((prev) => [
                    ...prev,
                    `Échec de l'importation de l'image: ${error?.message || 'Une erreur inattendue est survenue.'}`
                ]);
            }
        });

        // Display a success message if the operation is successful
        setIsPreviewOpen(false); // Close the modal

        // Reset the form fields
        setSelectedCategory('');
        setSelectedGroup('');

        // Reset medias array
        setMedia(null);

        // reset mediasQueue array
        setMediasQueue([]);

        // Reset unsent media state
        setUnsentMedia(false);
    };

    // Fetch media categories on component mount
    useEffect(() => {
        fetchMediaCategories();
    }, []);

    // Get unique groups from categories
    const uniqueGroups = [...new Set(mediaCategories.map(category => category.media_group_category?.name).filter(Boolean))];

    // Filter categories by selected group
    const filteredCategories = mediaCategories.filter(cat => !selectedGroup || cat.media_group_category?.name === selectedGroup);

    // Display a loading message while fetching data
    if (loading) return <div>Chargement...</div>;

    return (
        <React.Fragment>
            <div className='media-upload--form w-50'>

                {/* Display success and error messages */}
                {successMessages.map((msg, index) => (
                    <InfoBox key={`success-${index}`} type="validation" text={msg} onRemove={() => setSuccessMessages((prev) => prev.filter((_, i) => i !== index))} />
                ))}
                {errorMessages.map((msg, index) => (
                    <InfoBox key={`error-${index}`} type="error" text={msg} onRemove={() => setErrorMessages((prev) => prev.filter((_, i) => i !== index))} />
                ))}

                {/* Group selection */}
                <div className="media-upload--select">
                    <label>Groupe de catégories</label>
                    <select
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                    >
                        <option value="">Tous les groupes</option>
                        {uniqueGroups.map(group => (
                            <option key={group} value={group}>
                                {group}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Category group selection */}
                <div className="media-upload--select">
                    <label>Catégorie de la photo</label>
                    <select
                        value={selectedCategory || ''}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <option value="">Sélectionnez un groupe</option>
                        {filteredCategories.map(group => (
                            <option key={group.id} value={group.id}>
                                {group.name}
                            </option>
                        ))}
                    </select>
                </div>

                {/* Category name and group selection */}
                <div className="media-upload--toggle">
                    <label>Géolocalisation</label>
                    <ToggleButton
                        isActive={geolocation}
                        onToggle={onGeolocationChange}
                    />
                </div>

                {/* Category name and group selection */}
                <div className="media-upload--toggle">
                    <label>Horodatage</label>
                    <ToggleButton
                        isActive={timestamp}
                        onToggle={onTimestampChange}
                    />
                </div>

                {/* Display the form with the fields and submit buttons */}
                <CommonButton
                    icon={<AddCircle />}
                    label="Ajouter un autre média"
                    onClick={handleAddMediasQueue}
                    className="btn-save-media"
                />

                {mediasQueue?.length > 0 && (
                    <p className='media-tools--preview-tab__title'>
                        File d'attente des médias capturés ({mediasQueue.length})
                    </p>
                )}

                { /* Loop through the media tools and display them */ }
                <div className='media-tools--preview-tab'>
                    {/* Display the media loading message */}                    
                    {mediasQueue?.length > 0 && mediasQueue.map((mediaItem, i) => (
                        <React.Fragment key={`media_${i}`}>
                            {/* Display the media preview */}
                            {mediaItem && mediaItem.media ? (
                                <div className="media-preview--container">
                                    {/* Display the delete button */}
                                    <button
                                        className="media-preview--delete"
                                        onClick={() => handleDeleteMedia(i)}
                                    >
                                        ✕
                                    </button>

                                    {/* Display the media preview */}
                                    {mediaItem.media.type === 'video' ? (
                                        <video
                                            controls
                                            className="media-preview"
                                            aria-label="Preview de la vidéo capturée"
                                        >
                                            <source src={mediaItem.media.file} type="video/mp4" />
                                            Votre navigateur ne supporte pas la balise vidéo.
                                        </video>
                                    ) : mediaItem.media.type === 'image' ? (
                                        <img
                                            src={mediaItem.media.file}
                                            alt="Captured media"
                                            className="media-preview"
                                            aria-label="Preview de l'image capturée"
                                        />
                                    ) : (
                                        <span>Type de média non pris en charge</span>
                                    )}
                                </div>
                            ) : (
                                <span>Aucun média disponible</span>
                            )}
                        </React.Fragment>
                    ))}
                </div>

                {/* Display the form with the fields and submit buttons */}
                <CommonButton
                    icon={<Send />}
                    label={ ((mediasQueue.length === 0) || (!media && mediasQueue.length === 1)) ? "Associer le média au dossier" : "Associer les médias au dossier" }
                    onClick={handleConfirm}
                    className="btn-save-media"
                />

                { inAppointment && (
                    <React.Fragment>
                        {/* Display the form with the fields and submit buttons */}
                        <CommonButton
                            icon={<Undo />}
                            label={'Retour'}
                            onClick={() => secureBackNavigate()}
                            className="btn-save-media"
                        />
                    </React.Fragment>
                )}
            </div>

            { /* Display the media preview modal */ }
            {isPreviewOpen && (
                <CustomModal
                    open={isPreviewOpen}
                    onClose={() => setIsPreviewOpen(false)}
                    title="Confirmer l'envoi des médias"
                    handleSave={handleSave}
                    handleSaveLabel={`Confirmer l'envoi au serveur`}
                >
                    { /* Display the information box */ }
                    <InfoBox
                        type="warning"
                        text={`Vous êtes sur le point d'associer ${mediasQueue.length} média(s) au dossier. Veuillez confirmer l'envoi des médias.`}
                    />
                </CustomModal>
            )}

            {/* Modal de confirmation retour */}
            {isConfirmBackOpen && (
                <CustomModal
                open={isConfirmBackOpen}
                onClose={()=>setIsConfirmBackOpen(false)}
                title="Médias non envoyés"
                handleSave={() => navigate(-1)}
                handleSaveLabel="Confirmer et partir"
                onCloseLabel="Annuler et rester"
                >
                    <InfoBox
                        type="warning"
                        disabledCloseBtn={true}
                        text="Vous avez des médias non envoyés. Êtes-vous sûr de vouloir revenir en arrière sans les envoyer ?"
                    />
                </CustomModal>
            )}
        </React.Fragment>
    );
};

// Prop types for the MediaUploadForm component
MediaUploadForm.propTypes = {
    media: PropTypes.shape({
        file: PropTypes.any.isRequired,
        type: PropTypes.string.isRequired,
    }),
    setMedia: PropTypes.func.isRequired,
    folder: PropTypes.object.isRequired,
    geolocation: PropTypes.bool,
    timestamp: PropTypes.bool,
    onGeolocationChange: PropTypes.func.isRequired,
    onTimestampChange: PropTypes.func.isRequired
};

export default MediaUploadForm;
